import { useEmployeeDocuments } from './EmployeeFiles';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { addDays, berlinDate, formatCents, weekStart, type Employee, type EmployeeFile, type PayPerformance, type Paginated, type Shift, type Task } from '@kontor/shared';
import { api, queryString } from '../../api';
import { FactBoxPart, FactRow } from '../../components/CardLayout';
import { useSession } from '../../session';
import '../payroll/payroll.css';

const dateFormat = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
const stampFormat = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin' });
const num = (n: number) => n.toLocaleString('de-DE', { maximumFractionDigits: 2 });

/**
 * Infobox der Mitarbeiterkarte nach dem Vorbild der FactBox in Business Central: Übersicht, Zugang, Urlaub, Schichten dieser Woche, offene Aufgaben und Dokumente
 * auf einen Blick. Teile, für die dem Benutzer das Recht oder das Modul fehlt, erscheinen gar nicht.
 */
export function EmployeeFactBox({ employee: e }: { employee: Employee }) {
  const { can, hasModule } = useSession();
  const week = { from: weekStart(berlinDate()), to: addDays(weekStart(berlinDate()), 6) };

  const showShifts = hasModule('shifts') && can('shifts.write') && e.isActive;
  const shifts = useQuery({
    queryKey: ['shifts', week.from, week.to, e.id],
    queryFn: async () => (await api<{ data: Shift[] }>('GET', `/shifts${queryString({ from: week.from, to: week.to, employeeId: e.id })}`)).data,
    enabled: showShifts,
  });
  const showTasks = !!e.userId && hasModule('calendar') && can('calendar.read');
  const tasks = useQuery({
    queryKey: ['tasks', { group: 'open', assignedUserId: e.userId, perPage: 1 }],
    queryFn: () => api<Paginated<Task>>('GET', `/tasks${queryString({ group: 'open', assignedUserId: e.userId ?? undefined, perPage: 1 })}`),
    enabled: showTasks,
  });
  const storedDocs = useEmployeeDocuments(e.id) ?? [];
  const files = useQuery({ queryKey: ['employee-files', e.id], queryFn: async () => (await api<{ data: EmployeeFile[] }>('GET', `/employees/${e.id}/files`)).data });

  // Lohnkosten und Umsatz: nur mit dem Modul Löhne und dem Recht, Löhne zu sehen
  const showPay = hasModule('payroll') && can('payroll.read') && e.isActive;
  const perf = useQuery({ queryKey: ['payroll-performance', e.id], queryFn: async () => (await api<{ data: PayPerformance }>('GET', `/payroll/employees/${e.id}/performance`)).data, enabled: showPay });

  const v = e.vacation;
  const hours = (shifts.data ?? []).reduce((sum, s) => sum + s.hours, 0);

  return (
    <>
      <FactBoxPart title="Übersicht" storageKey="employee-overview">
        <FactRow label="Status">{e.isActive ? 'Aktiv' : 'Deaktiviert'}</FactRow>
        <FactRow label="Personalnr.">{e.personnelNumber}</FactRow>
        <FactRow label="Position">{e.position}</FactRow>
        <FactRow label="Abteilung">{e.department}</FactRow>
        <FactRow label="Wochenstunden">{e.weeklyHours === null ? null : num(e.weeklyHours)}</FactRow>
        <FactRow label="Eintritt">{e.hiredOn ? dateFormat.format(new Date(`${e.hiredOn}T12:00:00`)) : null}</FactRow>
        <FactRow label="Geändert">{stampFormat.format(new Date(e.updatedAt))}</FactRow>
      </FactBoxPart>

      {showPay && perf.data && (
        <FactBoxPart title={perf.data.measurable ? 'Lohnkosten und Fahrtumsatz' : 'Lohnkosten'} storageKey="employee-performance">
          <div className="payroll-perf">
            <div className="payroll-perf__tiles">
              <div className="payroll-perf__tile">
                <span className="muted small">Lohnkosten (12 Monate)</span>
                <strong>{formatCents(perf.data.costCents)}</strong>
                {perf.data.estimatedMonths > 0 && <span className="muted small">{perf.data.estimatedMonths === 1 ? '1 Monat geschätzt' : `${perf.data.estimatedMonths} Monate geschätzt`}</span>}
              </div>
              {perf.data.measurable && <div className="payroll-perf__tile"><span className="muted small">Fahrtumsatz (12 Monate)</span><strong>{formatCents(perf.data.revenueCents)}</strong></div>}
              {perf.data.ratio !== null && <div className="payroll-perf__tile"><span className="muted small">Umsatz je 1 € Lohnkosten</span><strong>{perf.data.ratio.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</strong></div>}
              {perf.data.measurable && <div className="payroll-perf__tile"><span className="muted small">Erledigte Aufträge</span><strong>{perf.data.months.reduce((n, m) => n + m.orderCount, 0)}</strong></div>}
            </div>
            <div className="payroll-perf__bars" role="img" aria-label="Lohnkosten je Monat, letzte zwölf Monate">
              {(() => {
                const max = Math.max(1, ...perf.data.months.map((m) => m.costCents));
                return perf.data.months.map((m) => <span key={m.month} className={`payroll-perf__bar${m.costEstimated ? '' : ' payroll-perf__bar--real'}`} style={{ height: `${Math.max(4, (m.costCents / max) * 100)}%` }} title={`${m.month}: ${formatCents(m.costCents)}${m.costEstimated ? ' (geschätzt)' : ''}`} />);
              })()}
            </div>
            <p className="muted small payroll-perf__legend">Lohnkosten je Monat. Dunkel: aus dem Lohnlauf, hell: geschätzt.</p>
            <ul className="payroll-perf__notes muted small">
              {perf.data.notes.map((n) => <li key={n}>{n}</li>)}
            </ul>
          </div>
        </FactBoxPart>
      )}

      <FactBoxPart title="Zugang" storageKey="employee-access">
        <FactRow label="Benutzer">{e.userName ?? 'Keiner'}</FactRow>
        <FactRow label="Systemrolle">{e.userRoleName}</FactRow>
      </FactBoxPart>

      {v && (
        <FactBoxPart title={`Urlaub ${v.year}`} storageKey="employee-vacation">
          {v.entitlement === null ? (
            <p className="muted small">Noch kein Urlaubsanspruch hinterlegt.</p>
          ) : (
            <div className="factbox__tiles">
              <div className="factbox__tile"><strong>{num(v.entitlement)}</strong><span>Anspruch</span></div>
              <div className="factbox__tile"><strong>{num(v.taken)}</strong><span>Genommen</span></div>
              <div className="factbox__tile"><strong>{num(v.requested)}</strong><span>Beantragt</span></div>
              <div className="factbox__tile"><strong>{num(v.remaining ?? 0)}</strong><span>Rest</span></div>
            </div>
          )}
        </FactBoxPart>
      )}

      {showShifts && (
        <FactBoxPart title="Schichten diese Woche" storageKey="employee-shifts">
          <FactRow label="Schichten">{shifts.isLoading ? '…' : String((shifts.data ?? []).length)}</FactRow>
          <FactRow label="Arbeitszeit">{shifts.isLoading ? '…' : `${num(hours)} Std.`}</FactRow>
          <p className="small"><Link to="/schichtplanung">Zur Schichtplanung</Link></p>
        </FactBoxPart>
      )}

      {showTasks && (
        <FactBoxPart title="Aufgaben" storageKey="employee-tasks">
          <FactRow label="Offen (zuständig)">{tasks.isLoading ? '…' : String(tasks.data?.meta.total ?? 0)}</FactRow>
          <p className="small"><Link to={`/kalender/aufgaben?assigned=${e.userId}`}>Aufgaben anzeigen</Link></p>
        </FactBoxPart>
      )}

      <FactBoxPart title="Dokumente" storageKey="employee-documents">
        <FactRow label="Dateien">{files.isLoading ? '…' : String((files.data ?? []).length + storedDocs.length)}</FactRow>
      </FactBoxPart>
    </>
  );
}
